import React, { useState, useEffect, useRef, useMemo } from "react";
import { FamilyInvitations } from "../../../../service";
import { toast } from "react-hot-toast";
import ButtonComponent from "../../../Ui/Button";
import Modal from "../../../Ui/Modal";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Box,
} from "@mui/material";

const FamilyInvitation = ({
  setModalState,
  propertyId,
  onSuccess,
  familyMembers = [],
}) => {
  const formikSubmitRef = useRef(null);
  const [selectedFamilyMembers, setSelectedFamilyMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  // Use provided family members (from AddFamilyMember) or fallback to empty list
  const computedMembers = useMemo(
    () =>
      (familyMembers && familyMembers.length ? familyMembers : []).map(
        (m, idx) => ({
          id: m.id ?? m.email ?? idx,
          name: m.name || "",
          email: m.email || "",
        })
      ),
    [familyMembers]
  );

  // Initialize all family members as selected
  useEffect(() => {
    if (computedMembers.length) {
      setSelectedFamilyMembers(computedMembers.map((m) => m.id));
      setSelectAll(true);
    } else {
      setSelectedFamilyMembers([]);
      setSelectAll(false);
    }
  }, [computedMembers]);

  const handleClose = () => {
    setModalState("");
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFamilyMembers([]);
      setSelectAll(false);
    } else {
      setSelectedFamilyMembers(computedMembers.map((member) => member.id));
      setSelectAll(true);
    }
    setError("");
  };

  const handleSelectFamilyMember = (memberId) => {
    if (selectedFamilyMembers.includes(memberId)) {
      setSelectedFamilyMembers(
        selectedFamilyMembers.filter((id) => id !== memberId)
      );
      setSelectAll(false);
    } else {
      const updated = [...selectedFamilyMembers, memberId];
      setSelectedFamilyMembers(updated);
      if (updated.length === computedMembers.length) setSelectAll(true);
    }
    setError("");
  };

  const handleSendInvites = async () => {
    if (!selectedFamilyMembers.length) {
      setError("Please select at least one family member to send invites");
      return;
    }
    if (!propertyId) {
      toast.error("Missing property id for invitations");
      return;
    }

    setSending(true);
    const selected = computedMembers.filter((m) =>
      selectedFamilyMembers.includes(m.id)
    );

    const results = await Promise.allSettled(
      selected.map((m) =>
        FamilyInvitations.create({
          family_member_name: m.name,
          family_member_email: m.email,
          property: propertyId,
        })
      )
    );

    const successes = results.filter((r) => r.status === "fulfilled").length;
    const failures = results.filter((r) => r.status === "rejected");

    if (successes) {
      toast.success(`Sent ${successes} invite${successes > 1 ? "s" : ""}`);
    }
    if (failures.length) {
      // Try to extract readable errors
      const firstErr = failures[0].reason;
      const msg =
        (firstErr &&
          (firstErr.response?.data?.detail || firstErr.response?.data?.error)) ||
        firstErr?.message ||
        "Some invites failed";
      toast.error(`${failures.length} failed: ${msg}`);
    }

    setSending(false);

    if (successes && onSuccess) onSuccess();
    // Close after action regardless; adjust if you prefer to keep open on partial failures
    handleClose();
  };

  return (
    <Modal
      style={{ maxWidth: "756px", width: "100%" }}
      open={true}
      onClose={handleClose}
      title="Invite Your Family Members To Join Properteasy"
      footer={
        <div className="flex w-full justify-between gap-2 items-center sm2:flex-row flex-col">
          <div className="flex items-center sm2:gap-4 gap-2">
            <Checkbox
              checked={selectAll}
              onChange={handleSelectAll}
              sx={{
                color: "#3CAB5C",
                "&.Mui-checked": {
                  color: "#3CAB5C",
                },
              }}
              disabled={sending}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "#333333", textWrap: "nowrap" }}
            >
              Select All
            </Typography>
          </div>

          <div className="flex sm2:gap-4 gap-2 md:flex-row flex-col w-full sm2:w-fit">
            <ButtonComponent
              paddingY="10px"
              bgColor="#f3f4f6"
              color="#000000"
              onClick={handleClose}
              disabled={sending}
            >
              Exit
            </ButtonComponent>
            <ButtonComponent
              paddingY="10px"
              bgColor="#3CAB5C"
              color="#FFFFFF"
              onClick={handleSendInvites}
              disabled={sending || selectedFamilyMembers.length === 0}
            >
              {sending ? "Sending..." : "Send Invites"}
            </ButtonComponent>
          </div>
        </div>
      }
    >
      <Box sx={{ width: "100%" }}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",

            paddingX: "10px",
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                <TableCell sx={{ width: "60px" }}></TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}
                >
                  Family Member
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#374151", fontSize: "14px" }}
                >
                  Send Via
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {computedMembers.map((member) => (
                <TableRow
                  key={member.id}
                  sx={{
                    "&:hover": { backgroundColor: "#f9fafb" },
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedFamilyMembers.includes(member.id)}
                      onChange={() => handleSelectFamilyMember(member.id)}
                      sx={{
                        color: "#3CAB5C",
                        "&.Mui-checked": {
                          color: "#3CAB5C",
                        },
                      }}
                      disabled={sending}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: "#111827", fontSize: "14px" }}
                    >
                      {member.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: "#111827" }}>
                      {member.email || "Email not provided"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Error message display */}
        {error && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography
              variant="body2"
              sx={{
                color: "#d32f2f",
                fontSize: "14px",
                fontWeight: 500,
                marginBottom: "10px",
              }}
            >
              {error}
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default FamilyInvitation;
